/**
 * SB5-B.2 — Contract Positions list page (`/contract-positions`).
 *
 * Source: `.feedback/26-05-22 AFTER SB4/03-page-contract-position.md`.
 *
 * Filter order (locked top-to-bottom):
 *   1. Search (position_code, project_name, site_name,
 *      assetDisplayLabel, external_ids JSONB — BE-side per Q3)
 *   2. Projects multiselect
 *   3. Sites async typeahead (BE typeahead — empty `q` returns nothing)
 *   4. Assets async typeahead (same pattern)
 *   5. States multiselect
 *   6. Show completed toggle (default OFF — hides COMPLETED/WITHDRAWN/REJECTED)
 *
 * Add button opens the Add modal; Edit pencil opens the Edit modal.
 *
 * TNCY-9 §b — Contract Positions is now a merged owner/member list. The BE
 * extended `GET /contract-positions` (projects/v1@2.6.0 / TNCY-8) with a
 * `scope=member` param + ownership metadata (`ownership`/`ownerTenantName`/
 * `role`) on `ContractPositionListItem`. This page fires both scopes and
 * merges client-side via the shared `mergeOwnerMember` primitive
 * (`useOwnerMemberContractPositions`); member rows are positions of member
 * contracts and carry the `<OwnershipBadge>`. The ownership column only
 * appears when the merged list actually has member rows, so an own-only
 * tenant's table is unchanged.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import type { GridSortModel } from "@mui/x-data-grid";
import {
  AsyncTypeaheadChipFilter,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  SearchField,
  TypeaheadChipFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import {
  useContractPositionsAssetsFacetQuery,
  useContractPositionsProjectsFacetQuery,
  useContractPositionsSitesFacetQuery,
} from "./useContractPositions.js";
import { useOwnerMemberContractPositions } from "./useOwnerMemberContractPositions.js";
import {
  CONTRACT_POSITION_STATES,
  CONTRACT_POSITIONS_SORT_KEYS,
  type ContractPositionListItem,
  type ContractPositionState,
  type ContractPositionsSortDir,
  type ContractPositionsSortKey,
  type ListContractPositionsParams,
} from "./types.js";
import { contractPositionStateLabel } from "./ContractPositionStatusChip.js";
import { buildContractPositionColumns } from "./columns.js";
import { ContractPositionEditModal } from "./ContractPositionEditModal.js";
import { ContractPositionAddModal } from "./ContractPositionAddModal.js";
import { FileTextIcon, PlusIcon } from "@aeriosiq/ui/icons";

const STATE_OPTIONS: readonly { id: ContractPositionState; name: string }[] =
  CONTRACT_POSITION_STATES.map((s) => ({
    id: s,
    name: contractPositionStateLabel(s),
  }));

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_BY: ContractPositionsSortKey = "positionCode";
const DEFAULT_SORT_DIR: ContractPositionsSortDir = "desc";

function parseSort(
  value: string | null,
): { key: ContractPositionsSortKey; dir: ContractPositionsSortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!(CONTRACT_POSITIONS_SORT_KEYS as readonly string[]).includes(k))
    return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as ContractPositionsSortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function ContractPositionsPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const includeCompleted = params.get("includeCompleted") === "1";
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  // SB6-CTRP-01 — same role set guards create-contract and create-
  // contract-position. The matrix cell for `project.create` is
  // `"conditional"` for tenant_admin / project_manager (predicate:
  // caller.tenantId === resource.tenantId), so the unconditional
  // useCanEditResource always returns false here. Switch to the
  // resource-aware helper so the Add button actually surfaces for
  // tenant_admin on /contract-positions. Christian [00:38:38]:
  // "Again I miss the button to add a contract position so I'm tenant
  // admin this option should be available to me."
  const canCreate = useCanEditResourceFor("project.create");

  const [editId, setEditId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const lastAppliedSearchRef = useRef(params.get("search") ?? "");
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const projectSelected = useMemo(
    () => parseCsv(params.get("project")),
    [params],
  );
  const siteSelected = useMemo(() => parseCsv(params.get("site")), [params]);
  const assetSelected = useMemo(() => parseCsv(params.get("asset")), [params]);
  const stateSelected = useMemo<ContractPositionState[]>(() => {
    const raw = parseCsv(params.get("state"));
    return raw.filter((s): s is ContractPositionState =>
      (CONTRACT_POSITION_STATES as readonly string[]).includes(s),
    );
  }, [params]);

  // Async-typeahead query state for sites + assets. Parent owns the
  // debounced input + hint Map so picked chips render labels even
  // when the server hit list doesn't currently include them.
  const [sitesInput, setSitesInput] = useState("");
  const debouncedSitesInput = useDebounced(sitesInput, 300);
  const sitesFacet = useContractPositionsSitesFacetQuery(debouncedSitesInput);
  const [siteHints, setSiteHints] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  useEffect(() => {
    const items = sitesFacet.data?.items;
    if (!items) return;
    setSiteHints((prev) => {
      const next = new Map(prev);
      for (const o of items) next.set(o.id, o.label);
      return next;
    });
  }, [sitesFacet.data]);

  const [assetsInput, setAssetsInput] = useState("");
  const debouncedAssetsInput = useDebounced(assetsInput, 300);
  const assetsFacet =
    useContractPositionsAssetsFacetQuery(debouncedAssetsInput);
  const [assetHints, setAssetHints] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  useEffect(() => {
    const items = assetsFacet.data?.items;
    if (!items) return;
    setAssetHints((prev) => {
      const next = new Map(prev);
      for (const o of items) next.set(o.id, o.label);
      return next;
    });
  }, [assetsFacet.data]);

  const queryParams = useMemo<ListContractPositionsParams>(() => {
    const out: ListContractPositionsParams = { page, pageSize };
    if (debouncedSearch.length > 0) out.q = debouncedSearch;
    out.sortBy = sortParsed?.key ?? DEFAULT_SORT_BY;
    out.sortDir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    out.includeCompleted = includeCompleted;
    if (projectSelected.length > 0) out.projectIds = projectSelected;
    if (siteSelected.length > 0) out.siteIds = siteSelected;
    if (assetSelected.length > 0) out.assetIds = assetSelected;
    if (stateSelected.length > 0) out.states = stateSelected;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeCompleted,
    projectSelected,
    siteSelected,
    assetSelected,
    stateSelected,
  ]);

  // TNCY-9 §b — fire both scopes + merge client-side; member rows carry the
  // `<OwnershipBadge>`. Own is the spine (its pending/error drives the list).
  const {
    rows: mergedRows,
    total,
    isPending,
    isError,
  } = useOwnerMemberContractPositions(queryParams);
  const { data: projectsFacet } = useContractPositionsProjectsFacetQuery();
  const projectOptions = projectsFacet?.items ?? [];

  function setSelectedFor(queryKey: string, values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete(queryKey);
        else next.set(queryKey, canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setStateSelected(values: readonly string[]): void {
    const canonical = [...new Set(values)]
      .filter((v): v is ContractPositionState =>
        (CONTRACT_POSITION_STATES as readonly string[]).includes(v),
      )
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("state");
        else next.set("state", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleIncludeCompleted(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("includeCompleted", "1");
        else next.delete("includeCompleted");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete("page");
        else next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(n));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const sortModel = useMemo<GridSortModel>(() => {
    const key = sortParsed?.key ?? DEFAULT_SORT_BY;
    const dir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    return [{ field: key, sort: dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (
        !(CONTRACT_POSITIONS_SORT_KEYS as readonly string[]).includes(
          head.field,
        )
      ) {
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const onEdit = useCallback((row: ContractPositionListItem) => {
    setEditId(row.contractPositionId);
  }, []);
  const onAdd = useCallback(() => {
    setAddOpen(true);
  }, []);

  const columns = useMemo(
    // R0627-05 — origin column always on (house icon for own rows too).
    () => buildContractPositionColumns({ onEdit, showOwnership: true }),
    [onEdit],
  );

  const rows = mergedRows;

  return (
    <DataPageShell title="Contract Positions">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Contract Positions toolbar"
        filtersTestId="contract-positions-toolbar-filters"
        settingsTestId="contract-positions-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search contract positions"
            />
            <TypeaheadChipFilter
              label="Projects"
              value={projectSelected}
              onChange={(next) => {
                setSelectedFor("project", next);
              }}
              options={projectOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            <AsyncTypeaheadChipFilter
              label="Sites"
              value={siteSelected}
              onChange={(next) => {
                setSelectedFor("site", next);
              }}
              options={sitesFacet.data?.items ?? []}
              loading={sitesFacet.isFetching}
              selectedHints={siteHints}
              onInputChange={setSitesInput}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            <AsyncTypeaheadChipFilter
              label="Assets"
              value={assetSelected}
              onChange={(next) => {
                setSelectedFor("asset", next);
              }}
              options={assetsFacet.data?.items ?? []}
              loading={assetsFacet.isFetching}
              selectedHints={assetHints}
              onInputChange={setAssetsInput}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            {/* SB6-CONS-03 — eagerOptions reveals the full enum on
                open; ContractPositionState is a small fixed list so
                typing-to-search is more friction than help. */}
            <TypeaheadChipFilter
              label="States"
              eagerOptions
              value={stateSelected}
              onChange={setStateSelected}
              options={STATE_OPTIONS}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.name}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeCompleted}
                  onChange={(_e, c) => {
                    toggleIncludeCompleted(c);
                  }}
                  inputProps={{
                    "aria-label": "Show completed contract positions",
                  }}
                />
              }
              label="Show completed"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            <Box sx={{ flex: 1 }} />
            {canCreate ? (
              <Button
                aria-label="Add Contract Position"
                onClick={onAdd}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                {/* SB6-D1 — Title-Case button text matching the canonical
                    Add Turbine / Add Component visual elsewhere. */}
                Add Contract Position
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load contract positions.
        </Typography>
      ) : rows.length === 0 ? (
        <ContractPositionsEmptyState />
      ) : (
        <DataTable<ContractPositionListItem & { id: string }>
          rows={rows.map((r) => ({ ...r, id: r.contractPositionId }))}
          columns={columns as never}
          ariaLabel="Contract Positions"
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pinnedActionColumn
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="contract positions"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="contract-positions-footer-total"
      />

      <ContractPositionEditModal
        id={editId}
        onClose={() => {
          setEditId(null);
        }}
      />
      <ContractPositionAddModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onCreated={() => undefined}
      />
    </DataPageShell>
  );
}

function ContractPositionsEmptyState(): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <FileTextIcon
        sx={{ fontSize: 48, color: "text.secondary" }}
        aria-hidden
      />
      <Typography variant="h6" color="text.primary">
        No contract positions yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Contract positions appear here once they are created for your tenant.
      </Typography>
    </Box>
  );
}
